/**
 * Package-PRIVATE ordered-stack seam (OpenSpec `monorepo-kit-editor` task 3.1;
 * design D4). Migration machinery — NOT a supported deep import or consumer
 * contract: this module is unreachable through the exports map, deep imports
 * are rejected by boundary validation, and no `/extensions` door exists.
 *
 * Contract (fixed by design D4, verified against the frozen app-side trace in
 * `src/components/tiptap/markdown-native/extension-kit.trace.test.ts`):
 *
 * 1. The canonical base reproduces the legacy `buildMarkdownNativeExtensions`
 *    exactly — same extension names, kinds, options, native priorities, and
 *    literal array order. No sorting, filtering, deduplication, renaming,
 *    replacing, cloning, or revalidation after a builder returns.
 * 2. Cross-builder JavaScript reference equality is neither required nor
 *    permitted as a reason to share stateful extension instances across
 *    Editors; observable fresh-versus-shared creation cadence matches the
 *    legacy builder.
 * 3. Interactive mounts keep constructing their complete final
 *    base-plus-live-tail arrays at the legacy app path; they never import this
 *    helper. It exists for `/core` conversion and package-internal surfaces
 *    only (populated by extraction slice 5.3, after the phase-4/5 seam
 *    inversions move the kernel ingredients into the package).
 */
import { textblockTypeInputRule, type AnyExtension } from '@tiptap/core';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import Heading, { type Level } from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import { BulletList, ListItem, ListKeymap, OrderedList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import Text from '@tiptap/extension-text';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import { Markdown, type MarkdownExtensionOptions } from '@tiptap/markdown';

import { documentMarkdownSpec } from '../document-markdown';
import { MarkdownHardBreak } from '../hard-break-line-normalization';
import { listItemMarkdownSpec } from '../list-item-markdown';
import { paragraphMarkdownSpec } from '../paragraph-markdown';
import { createMarkdownNativeMarked } from '../sections/section-markdown';
import { MarkdownSectionNodeBase } from '../sections/section-node-base';
import { tableMarkdownSpec, withCellAlign } from '../table-markdown';

/** The canonical Scribe stack: an ordered, read-only TipTap extension array. */
export type InternalOrderedExtensionStack = readonly AnyExtension[];

/**
 * Options for the package-private canonical base builder. Mirrors the legacy
 * builder's construction-only input surface (currently the placeholder).
 */
export interface InternalCanonicalStackOptions {
  placeholder: string | (() => string);
}

/**
 * Markdown pinned to the kit's own marked instance (ATX-only headings plus the
 * html-block section boundary), fresh per call so the overrides and the node
 * tokens registered at editor init stay off the global singleton. Unpinned,
 * `@tiptap/markdown` lexes with its own nested marked copy that never sees
 * either override. The cast bridges the package's `Marked` (v16 class
 * instance) to the option's global-callable type (tiptap's bundled v15); only
 * instance members are used at runtime. Mirrors the app kit's
 * `buildMarkdownNativeMarkdown`.
 */
const buildCanonicalMarkdown = () =>
  Markdown.configure({
    marked: createMarkdownNativeMarked() as unknown as MarkdownExtensionOptions['marked'],
  });

// Levels that exist in the schema. Level 1 is kept because the section heading
// is a `heading` with level 1 — but it is never created as an inline heading.
const HEADING_LEVELS: Level[] = [1, 2, 3, 4, 5];
// Levels the user can create inline. `# ` (H1) is reserved as the section
// delimiter, so it is excluded here.
const INLINE_HEADING_LEVELS: Level[] = [2, 3, 4, 5];

/**
 * Heading rendering H2–H5 only. `#` (H1) is the section delimiter — its `# `
 * input rule lives on the section node, so the default `# `→H1 rule and
 * `Mod-Alt-1` shortcut are dropped. Each inline level matches an exact hash
 * count: Heading's default `#{1,n}` rule would let `# ` create an H2 once the
 * H1 rule is gone, so `^(#{2})\s$` etc. keep `# ` falling through to the
 * section rule.
 */
const canonicalHeading = Heading.configure({ levels: HEADING_LEVELS }).extend({
  addInputRules() {
    return INLINE_HEADING_LEVELS.map((level) =>
      textblockTypeInputRule({
        find: new RegExp(`^(#{${level}})\\s$`),
        type: this.type,
        getAttributes: { level },
      })
    );
  },
  addKeyboardShortcuts() {
    return Object.fromEntries(
      INLINE_HEADING_LEVELS.map((level) => [
        `Mod-Alt-${level}`,
        () => this.editor.commands.toggleHeading({ level }),
      ])
    );
  },
});

/**
 * Typography with most automatic replacements disabled (arrows only) and the
 * dropcursor's shared appearance. Package-owned per task 5.3's
 * "package-owned implementation proven against the affected trace" route: the
 * app's equivalents live in a nobody-owned file that vMarkdown and vJSON also
 * import, so they are not relocated — the parity trace is the anti-drift gate,
 * and a change on either side fails it loudly.
 */
const canonicalTypography = Typography.configure({
  closeDoubleQuote: false,
  closeSingleQuote: false,
  copyright: false,
  ellipsis: false,
  emDash: false,
  laquo: false,
  leftArrow: '←',
  multiplication: false,
  notEqual: false,
  oneHalf: false,
  oneQuarter: false,
  openDoubleQuote: false,
  openSingleQuote: false,
  plusMinus: false,
  raquo: false,
  registeredTrademark: false,
  rightArrow: '→',
  servicemark: false,
  superscriptThree: false,
  superscriptTwo: false,
  threeQuarters: false,
  trademark: false,
});

const canonicalDropcursor = Dropcursor.configure({
  width: 2,
  class: 'ProseMirror-dropcursor',
  color: '#7dd3fc',
});

/**
 * The package-private canonical base stack for `/core` conversion and
 * package-internal surfaces (task 5.3). Reproduces the legacy app builder's
 * literal order, runtime names, kinds, native priorities, and effective
 * options — the contract frozen by the app-side canonical stack trace, and
 * mirrored by this module's own parity suite.
 *
 * Two live-only node-view bindings are deliberately absent, because a
 * conversion editor is headless (`element: null`) and never instantiates a
 * node view. Neither is part of the traced contract (names/kinds/options/
 * priorities/order), so both stacks trace identically apart from the section
 * node's telemetry option:
 *
 * 1. the interactive React section overlay — this stack uses the React-free
 *    `MarkdownSectionNodeBase`, whose `onSectionCreated` defaults to `null`
 *    (no telemetry: a headless editor never runs the typing pipeline), where
 *    the app stack injects the analytics adapter (design D4/D4.4, task 5.4);
 * 2. the horizontal-rule DOM wrapper, a click-target affordance for the live
 *    editor only.
 *
 * Interactive mounts keep building their complete final array at the app path
 * and never import this helper.
 */
/**
 * Mirrors the app kit's `readOnlyCodeBlock`: a code block that cannot be
 * typed into existence — no ` ``` `/`~~~` input rules and no `Mod-Alt-c`. The
 * node stays registered so stored fences and four-space indented content
 * still load, render and round-trip, which the section boundary depends on.
 *
 * Mirrored here because the canonical base must be the SAME stack on both
 * sides (task 3.2). Input rules never fire during headless conversion, so the
 * output is unaffected either way — but a stack that differs in plugin count
 * breaks the parity contract, and the 3.5 runtime gate correctly rejected it.
 *
 * The parent's `codeBlockVSCodeHandler` plugin pastes Monaco-family copies as
 * typed code blocks — the last direct authoring path. Dropping it is inert
 * here (a headless renderer receives no paste events) and keeps the plugin
 * count equal to the app kit's; it goes by key so a plugin added by an
 * upstream upgrade survives and shows up in the runtime snapshot rather than
 * vanishing.
 */
const readOnlyCodeBlock = CodeBlock.extend({
  addInputRules: () => [],
  addKeyboardShortcuts() {
    const shortcuts = { ...this.parent?.() };
    delete shortcuts['Mod-Alt-c'];
    return shortcuts;
  },
  addProseMirrorPlugins() {
    return (this.parent?.() ?? []).filter(
      (plugin) =>
        !String((plugin as { key?: unknown }).key).startsWith('codeBlockVSCodeHandler')
    );
  },
});

/**
 * Mirrors the app kit's underline override: the stock
 * `@tiptap/extension-underline` serializes the mark as the markdown-it-ins
 * token `++text++`, which no consumer of this markdown renders as underline.
 * Emit `<u>...</u>` — legal raw HTML in markdown and the stored form
 * everywhere else — so the canonical HTML→markdown direction (copy
 * `text/plain` sibling, copy-settings flavor) agrees with the live kit. The
 * inherited `++` tokenizer stays, so legacy token-form notes still hydrate.
 */
const canonicalUnderline = Underline.extend({
  renderMarkdown: (node, helpers) => `<u>${helpers.renderChildren(node)}</u>`,
});

export const buildInternalCanonicalStack = ({
  placeholder,
}: InternalCanonicalStackOptions): InternalOrderedExtensionStack => [
  buildCanonicalMarkdown(),
  Document.extend(documentMarkdownSpec),
  Paragraph.extend(paragraphMarkdownSpec),
  Text,
  Bold,
  Code,
  Italic,
  Strike,
  canonicalUnderline,
  Link.configure({ openOnClick: false }),
  Blockquote,
  BulletList,
  OrderedList,
  ListItem.extend(listItemMarkdownSpec),
  readOnlyCodeBlock,
  // Normalized `setHardBreak`: a press that would create a
  // break-only line becomes empty-paragraph structure instead of an
  // unrepresentable hardBreak run.
  MarkdownHardBreak,
  HorizontalRule,
  ListKeymap,
  Gapcursor,
  History,
  canonicalHeading,
  canonicalDropcursor,
  canonicalTypography,
  Placeholder.configure({
    placeholder,
    showOnlyCurrent: false,
  }),
  MarkdownSectionNodeBase,
  Table.extend({ draggable: true, ...tableMarkdownSpec }).configure({ resizable: true }),
  TableRow,
  withCellAlign(TableHeader),
  withCellAlign(TableCell),
];
